var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { EVENT_NAME_MESSAGE, messageVersion } from './Constants';
import { MessageType, SatelliteTimeoutError, } from './Interfaces';
import { maxRetryCount, maxRetryTimeInMillis } from './Satellite';
import { SatelliteEventBus } from './SatelliteEventBus';
import { generateNewMessageId, validateMessage, validMessageType, } from './SatelliteUtil';
// Handshake timeout derived from parent Satellite timeout plus some buffer.
export const handshakeTimeoutInMillis = maxRetryCount * maxRetryTimeInMillis + 5000;
// (Service) request timeout should be greater than highest timeout of all services plus some buffer.
// In the future, individual timeout metadata could passed through satellite props
export const fetchDataTimeoutInMillis = 90000;
/**
 * Enum used to indicate which Satellite this client is talking to.
 */
export var SatelliteClientHandshakeTarget;
(function (SatelliteClientHandshakeTarget) {
    /**
     * Satellite (not SatelliteClient) lives in Aviary shell app
     */
    SatelliteClientHandshakeTarget[SatelliteClientHandshakeTarget["SHELL_APP"] = 0] = "SHELL_APP";
    /**
     * Satellite (not SatelliteClient) lives in Service Worker
     */
    SatelliteClientHandshakeTarget[SatelliteClientHandshakeTarget["SERVICE_WORKER"] = 1] = "SERVICE_WORKER";
})(SatelliteClientHandshakeTarget || (SatelliteClientHandshakeTarget = {}));
/**
 * Satellite library enables an application to embed another application that is hosted on a separate environment through the use of Webviews or Iframes.
 * It provides useful abstraction over Iframe & Webview and handles the complexities such as message passing, dealing with promises etc from the parent application.
 * Satellite is used by the parent application while SatelliteClient is used by the guest application.
 *
 * Sample Usage: const satelliteClient = new SatelliteClient();
 * const handshakePromise = satelliteClient.completeHandshake();
 * handshakePromise.then((satelliteProps) => {
 *     console.log('Handshake is complete');
 * });
 * Once a new Satellite client is instantiated,
 *  1. the guest application is up for receiving the handshake messages from parent
 *  2. respond back to the handshake messages.
 *  3. exposing all the functionalities passed by the parent application during handshake to the guest application, and a satellite token
 *  4. satellite token must be passed for all requests
 * The Satellite client has handshake timeout before giving up waiting for parent, and separate timeout for each service call. Both much longer than
 * it should take.
 */
export class SatelliteClient {
    constructor(props) {
        this.buildSatelliteMessage = this.buildSatelliteMessage.bind(this);
        this.fetchData = this.fetchData.bind(this);
        this.completeHandshake = this.completeHandshake.bind(this);
        this.processHandshakeEvent = this.processHandshakeEvent.bind(this);
        this.handleResizeEvent = this.handleResizeEvent.bind(this);
        this.handleTitleChangeEvent = this.handleTitleChangeEvent.bind(this);
        this.generateServiceFunction = this.generateServiceFunction.bind(this);
        this.initSatelliteEventClientBuses = this.initSatelliteEventClientBuses.bind(this);
        this.fetchHandler = this.fetchHandler.bind(this);
        this.isPrefetch = false;
        this.uidToFetchContext = {};
        this.handShakeTarget =
            (props === null || props === void 0 ? void 0 : props.handShakeTarget) || SatelliteClientHandshakeTarget.SHELL_APP;
        this.satelliteServicesOptions = (props === null || props === void 0 ? void 0 : props.satelliteServicesOptions) || {};
    }
    /**
     * This is a type of message listeners that listens to messages of type 'handshake'.
     * It is responsible for adding all the properties to the window object that are passed in the handshake by the parent application.
     * Acknowledges the handshake by responding back to the parent application.
     * This method returns a promise that is resolved when the handshake is complete.
     */
    completeHandshake() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                this.getHandshakeEvent()
                    .then((e) => {
                    console.debug('Received handshake message: ', e);
                    const message = e.data;
                    const satelliteProps = this.processHandshakeEvent(message, e.ports[0]);
                    // eslint-disable-next-line no-restricted-syntax,@typescript-eslint/no-explicit-any
                    if (window.ResizeObserver) {
                        // Register resize event listener
                        // eslint-disable-next-line no-restricted-syntax,@typescript-eslint/no-explicit-any
                        const resizeObserver = new window.ResizeObserver(this.handleResizeEvent);
                        resizeObserver.observe(document.body, {
                            box: 'border-box',
                        });
                    }
                    // Select the target node as document title.
                    const target = document.querySelector('title');
                    // Check if target exists and if it is a valid node to be observed
                    if (
                    // eslint-disable-next-line no-restricted-syntax,@typescript-eslint/no-explicit-any
                    window.MutationObserver &&
                        target &&
                        target.nodeType) {
                        // Create an observer instance for component title change.
                        // Reference: https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver.
                        // eslint-disable-next-line no-restricted-syntax,@typescript-eslint/no-explicit-any
                        const mutationObserver = new window.MutationObserver(this.handleTitleChangeEvent);
                        // Pass in the target node and the observer options.
                        mutationObserver.observe(target, {
                            subtree: true,
                            characterData: true,
                            childList: true,
                        });
                    }
                    if (!this.isPrefetch) {
                        resolve(satelliteProps);
                    }
                })
                    .catch((err) => {
                    reject(err);
                });
            });
        });
    }
    /**
     * Respond to the handshake message. Save the MessagePort for future use.
     * Register additional listeners.
     */
    processHandshakeEvent(message, port, options) {
        var _a;
        this.satellitePort = port;
        this.satellitePort.addEventListener(EVENT_NAME_MESSAGE, this.fetchHandler);
        this.satellitePort.start();
        const props = message.props;
        const uid = message.uid;
        this.isPrefetch = !!message.isPrefetch;
        this.satelliteToken = message.satelliteToken;
        let servicesArray = [];
        if (props.services) {
            servicesArray = props
                .services;
        }
        const services = {};
        servicesArray.forEach((service) => {
            services[service] = this.generateServiceFunction(service);
        });
        let eventsArray = [];
        if (props.events) {
            eventsArray = props.events;
        }
        let events = {};
        if (eventsArray && eventsArray.length > 0) {
            console.debug(`[SatelliteClient] Setting long-lived event listener and EventBuses for ${JSON.stringify(eventsArray)}`);
            events = this.initSatelliteEventClientBuses(this.satellitePort, eventsArray);
        }
        const componentTitle = (_a = globalThis.document) === null || _a === void 0 ? void 0 : _a.title;
        const satelliteProps = Object.assign(Object.assign({}, props), { services,
            events });
        // eslint-disable-next-line no-restricted-syntax,@typescript-eslint/no-explicit-any
        window.satelliteProps = satelliteProps;
        this.satellitePort.postMessage(Object.assign(Object.assign({}, this.buildSatelliteMessage(MessageType.HANDSHAKE_REPLY, uid)), { componentTitle, options: options, remoteCardMetrics: this.getRemoteCardMetrics() }));
        return satelliteProps;
    }
    initSatelliteEventClientBuses(satellitePort, eventsArray) {
        const eventBuses = {};
        eventsArray.forEach((serializedEventBus) => {
            eventBuses[serializedEventBus.eventName] = new SatelliteEventBus(serializedEventBus.config);
        });
        const satelliteEventsHandler = (ev) => {
            const messageValidation = validateMessage(ev, this.appOrigin, this.satelliteToken);
            if (messageValidation !== validMessageType.VALID) {
                console.warn(`[SatelliteClient] Invalid Satellite Event Message ${JSON.stringify(ev.data)}`);
                return;
            }
            const satelliteEventPayload = ev.data;
            if (satelliteEventPayload.type !== MessageType.USER_EVENT_PAYLOAD) {
                return;
            }
            const eventName = satelliteEventPayload.eventName;
            if (!eventBuses[eventName]) {
                console.warn(`[SatelliteClient] Got USER_EVENT_PAYLOAD for a non-registered event: ${eventName}. Registered events: ${JSON.stringify(eventsArray)}`);
                return;
            }
            const eventBus = eventBuses[eventName];
            console.debug('[SatelliteClient] Dispatching: ', satelliteEventPayload);
            eventBus.dispatch(satelliteEventPayload.payload);
        };
        satellitePort.addEventListener(EVENT_NAME_MESSAGE, satelliteEventsHandler);
        return eventBuses;
    }
    /**
     * Listen for and return a handshake event.
     */
    getHandshakeEvent() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                const handshakeListenerHost = this.handShakeTarget ===
                    SatelliteClientHandshakeTarget.SERVICE_WORKER
                    ? navigator.serviceWorker
                    : window;
                const handshakeListener = (e) => {
                    const messageValidation = validateMessage(e, this.appOrigin, this.satelliteToken);
                    if (messageValidation !== validMessageType.VALID) {
                        return;
                    }
                    if (e.data.type !== MessageType.HANDSHAKE) {
                        return;
                    }
                    handshakeListenerHost.removeEventListener(EVENT_NAME_MESSAGE, handshakeListener);
                    clearTimeout(handshakeTimerId);
                    resolve(e);
                };
                const handshakeTimerId = setTimeout(() => {
                    handshakeListenerHost.removeEventListener(EVENT_NAME_MESSAGE, handshakeListener);
                    if (!this.isPrefetch) {
                        const errMsg = `Waited too long to complete handshake. Gave up after ${handshakeTimeoutInMillis} ms.`;
                        console.error(errMsg);
                        reject(new SatelliteTimeoutError(errMsg));
                    }
                }, handshakeTimeoutInMillis);
                console.debug('Starting to listen for handshake');
                handshakeListenerHost.addEventListener(EVENT_NAME_MESSAGE, handshakeListener);
            });
        });
    }
    /**
     * This API is to be used by service worker clients to request a handshake from Satellite in service worker
     * by sending a postMessage with MessageType.SERVICE_WORKER_CLIENT_REQUEST_HANDSHAKE.
     * @returns SatelliteClient
     */
    requestHandshakeWithServiceWorker() {
        if (this.handShakeTarget !==
            SatelliteClientHandshakeTarget.SERVICE_WORKER) {
            throw new Error('This API should only be called when handshaking with Service Worker.');
        }
        navigator.serviceWorker.controller.postMessage({
            type: MessageType.SERVICE_WORKER_CLIENT_REQUEST_HANDSHAKE,
        });
        return this;
    }
    /**
     * Build a basic satellite message.
     */
    buildSatelliteMessage(type, uid) {
        return {
            type,
            uid,
            messageVersion,
            satelliteToken: this.satelliteToken,
            timestamp: Date.now(),
        };
    }
    fetchHandler(e) {
        const messageValidation = validateMessage(e, this.appOrigin, this.satelliteToken);
        if (messageValidation !== validMessageType.VALID) {
            return;
        }
        const message = e.data;
        if (!this.uidToFetchContext[message.uid]) {
            return;
        }
        if (message.type === MessageType.REPLY) {
            if (this.uidToFetchContext[message.uid].timeoutId) {
                clearTimeout(this.uidToFetchContext[message.uid].timeoutId);
            }
            this.uidToFetchContext[message.uid].resolve(message.response);
            delete this.uidToFetchContext[message.uid];
            return;
        }
        else if (message.type === MessageType.REPLY_EXCEPTION) {
            if (this.uidToFetchContext[message.uid].timeoutId) {
                clearTimeout(this.uidToFetchContext[message.uid].timeoutId);
            }
            this.uidToFetchContext[message.uid].reject(message.error);
            delete this.uidToFetchContext[message.uid];
            return;
        }
    }
    /**
     * Private utility for making asynchronous communication to the parent application. It returns a promise immediately
     * which is resolved when the parent application responds back with a message.
     * @param property it denotes the functionality name
     * @param inputs it denotes the inputs needed for executing the functionality.
     */
    fetchData(property, inputs) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                var _a;
                const uid = generateNewMessageId();
                let fetchDataTimeout;
                // TODO: update in https://sim.amazon.com/issues/caracara-26464
                if (!((_a = this.satelliteServicesOptions[property]) === null || _a === void 0 ? void 0 : _a.shouldNotTimeout)) {
                    fetchDataTimeout = setTimeout(() => {
                        delete this.uidToFetchContext[uid];
                        const errMsg = `Waited too long for '${property}' service call.
                                    Gave up after ${fetchDataTimeoutInMillis} ms.`;
                        console.error(errMsg + ' Request:', inputs);
                        reject(new SatelliteTimeoutError(errMsg));
                    }, fetchDataTimeoutInMillis);
                }
                this.uidToFetchContext[uid] = {
                    reject: reject,
                    resolve: resolve,
                    timeoutId: fetchDataTimeout,
                };
                const inputsArray = [];
                for (let arg = 0; arg < inputs.length; ++arg) {
                    inputsArray.push(inputs[arg]);
                }
                const request = Object.assign(Object.assign({}, this.buildSatelliteMessage(MessageType.REQUEST, uid)), { property,
                    inputsArray });
                console.debug('Posting Message to App: ' + JSON.stringify(request));
                // Then ask app for information
                this.satellitePort.postMessage(request);
            });
        });
    }
    /**
     * Utility for generating a function that calls the exposed functionality by the parent app through message passing.
     * @param name
     */
    generateServiceFunction(name) {
        return (...args) => __awaiter(this, void 0, void 0, function* () {
            return this.fetchData(name, args);
        });
    }
    /**
     * Handle event for resize.
     */
    handleResizeEvent() {
        const uid = generateNewMessageId();
        const request = Object.assign(Object.assign({}, this.buildSatelliteMessage(MessageType.RESIZE, uid)), { height: document.body.scrollHeight });
        this.satellitePort.postMessage(request);
    }
    /**
     * Handle event for title change.
     */
    handleTitleChangeEvent() {
        const uid = generateNewMessageId();
        const request = Object.assign(Object.assign({}, this.buildSatelliteMessage(MessageType.TITLE_CHANGE, uid)), { title: document.title });
        this.satellitePort.postMessage(request);
    }
    /**
     * Retrieve the combined fetched resource size for the remote solve card.
     * @returns Object containing the hostname and size of the content (In MB) if fetched from cloudfront.
     */
    getRemoteCardMetrics() {
        if (location.origin.indexOf('cloudfront') == -1) {
            return undefined;
        }
        const pathName = location.pathname.split('/');
        const htmlPath = location.origin + location.pathname;
        const jsPath = this.getJSBundlePath();
        if (!jsPath) {
            console.warn('Unable to determine path to js bundle, will not report size');
            return undefined;
        }
        const remoteCardSize = {
            htmlSize: JSON.parse(JSON.stringify(performance.getEntriesByName(htmlPath)))[0].decodedBodySize,
            jsSize: JSON.parse(JSON.stringify(performance.getEntriesByName(jsPath)))[0].decodedBodySize,
        };
        const remoteCardDownloadTime = {
            downloadTimeHTML: JSON.parse(JSON.stringify(performance.getEntriesByName(htmlPath)))[0].duration,
            downloadTimeJS: JSON.parse(JSON.stringify(performance.getEntriesByName(jsPath)))[0].duration,
        };
        return {
            hostName: location.hostname + pathName.slice(0, 2).join('/'),
            remoteCardSize: (remoteCardSize.htmlSize + remoteCardSize.jsSize) /
                (1024 * 1024),
            remoteCardDownloadTime: remoteCardDownloadTime.downloadTimeHTML +
                remoteCardDownloadTime.downloadTimeJS,
        };
    }
    getJSBundlePath() {
        const pathName = location.pathname.split('/');
        const potentialBundleFiles = ['index.js', 'index_remote.js'];
        const potentialBundlePaths = potentialBundleFiles.map((fileName) => {
            return (location.origin +
                pathName.slice(0, pathName.length - 1).join('/') +
                '/' +
                fileName);
        });
        const validEntry = potentialBundlePaths.find((bundlePath) => {
            return performance.getEntriesByName(bundlePath)[0];
        });
        return validEntry;
    }
}
//# sourceMappingURL=SatelliteClient.js.map